/** A grade meter: three 28px segments labelled A, B and C, with a marker under the current grade
 * (ADR-0212, P7). It replaces a needle gauge, whose position has to be judged by eye.
 *
 * Grades are ordinal and the product's own (A metered, B calculated from a document, C stated). The
 * caller names the current grade (`current`) - nothing here derives it - and gives each grade a
 * short `text` for the tooltip and the table ("Metered or billed"). Each segment holds its letter in
 * `--on-fill` over the grade's fill; the current segment is full strength and the others are pale
 * with an outline, so the answer does not rest on colour: the marker is a triangle with the word
 * "Current" under the segment, and the segment says `aria-current`. Segments are focusable images
 * with the tooltip on focus and hover; a grade with a `receipt` opens it when the caller passes
 * `onOpenReceipt`. Inside a `ChartCard` the Table button shows the same grades as a table.
 */

import { useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import chart from "./Chart.module.css";
import { ChartTable } from "./ChartTable";
import { ChartTooltip } from "./ChartTooltip";
import styles from "./GradeMeter.module.css";
import { useChartAsTable } from "./chartView";
import { canOpen, receiptProps } from "./receipt";
import type { OpenReceipt } from "./receipt";
import { useChartTip } from "./useChartTip";

export interface GradeSegment {
  /** The letter: `A`. */
  grade: string;
  /** What the grade means, as the caller words it. */
  text: string;
  /** A count or share for the grade, as printed (`12 records`). */
  figure?: string;
  receipt?: string;
}

/** Grade fills, A (strongest) first. Tokens from the data-quality ramp (`--q-a` to `--q-d`). */
const FILLS = ["var(--q-a)", "var(--q-b)", "var(--q-c)", "var(--q-d)"] as const;

export function GradeMeter({
  grades,
  current,
  caption,
  onOpenReceipt,
  testId = "grade-meter",
}: {
  /** In order, strongest first. Three is the design; up to four are drawn. */
  grades: readonly GradeSegment[];
  /** The current grade's letter. */
  current: string;
  caption: string;
  onOpenReceipt?: OpenReceipt;
  testId?: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const refs = useRef(new Map<number, HTMLElement>());
  const asTable = useChartAsTable();
  const { tip, show, hide } = useChartTip(wrap);
  const [focus, setFocus] = useState(Math.max(0, grades.findIndex((g) => g.grade === current)));

  const sentence = (g: GradeSegment) =>
    `Grade ${g.grade}: ${g.text}${g.figure ? `, ${g.figure}` : ""}${g.grade === current ? ". Current grade" : ""}${canOpen(g.receipt, onOpenReceipt) ? ". Opens the receipt" : ""}`;

  if (asTable) {
    return (
      <div className={chart.chart} data-testid={testId}>
        <ChartTable
          visible
          caption={caption}
          head={["Grade", "Meaning", "Figure"]}
          rows={grades.map((g) => [g.grade === current ? `${g.grade} (current)` : g.grade, g.text, g.figure ?? ""])}
        />
      </div>
    );
  }

  const point = (index: number) => {
    const g = grades[index];
    if (g) show(refs.current.get(index), `Grade ${g.grade}${g.grade === current ? " · current" : ""}`, [g.text, ...(g.figure ? [g.figure] : [])]);
  };
  const move = (index: number) => {
    const next = Math.max(0, Math.min(grades.length - 1, index));
    setFocus(next);
    refs.current.get(next)?.focus();
  };
  const onKey = (event: KeyboardEvent<HTMLElement>, index: number) => {
    if (event.key === "ArrowRight") move(index + 1);
    else if (event.key === "ArrowLeft") move(index - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(grades.length - 1);
    else if (event.key === "Escape") hide();
    else return;
    event.preventDefault();
  };

  return (
    <div ref={wrap} className={`${chart.chart} ${styles.wrap}`} data-testid={testId}>
      <div
        className={styles.row}
        role={grades.some((g) => canOpen(g.receipt, onOpenReceipt)) ? "toolbar" : "group"}
        aria-orientation={grades.some((g) => canOpen(g.receipt, onOpenReceipt)) ? "horizontal" : undefined}
        aria-label={caption}
      >
        {grades.map((g, index) => {
          const open = receiptProps(g.receipt, g.grade, onOpenReceipt);
          const on = g.grade === current;
          return (
            <div key={g.grade} className={styles.cell}>
              <div
                ref={(node) => {
                  if (node) refs.current.set(index, node);
                  else refs.current.delete(index);
                }}
                role={open.onClick ? "button" : "img"}
                tabIndex={focus === index ? 0 : -1}
                aria-label={sentence(g)}
                aria-current={on ? "true" : undefined}
                className={`${styles.segment} ${on ? styles.on : styles.off} ${open.onClick ? styles.openable : ""}`}
                style={{ ["--grade" as string]: FILLS[Math.min(index, FILLS.length - 1)] }}
                data-testid="grade-segment"
                data-grade={g.grade}
                data-current={on ? "true" : undefined}
                data-receipt={open["data-receipt"]}
                onClick={open.onClick}
                onKeyDown={(event) => {
                  onKey(event, index);
                  if (!event.defaultPrevented) open.onKeyDown?.(event);
                }}
                onFocus={() => {
                  setFocus(index);
                  point(index);
                }}
                onBlur={hide}
                onPointerEnter={() => {
                  point(index);
                }}
                onPointerLeave={hide}
              >
                <span aria-hidden="true">{g.grade}</span>
              </div>
              {on ? (
                <span className={styles.marker} aria-hidden="true" data-testid="grade-marker">
                  <span className={styles.caret} />
                  <span className={styles.markerWord}>Current</span>
                </span>
              ) : (
                <span className={styles.markerSpace} aria-hidden="true" />
              )}
            </div>
          );
        })}
      </div>
      <ChartTooltip anchor={tip?.anchor ?? null}>
        <b>{tip?.title}</b>
        {tip?.lines.map((line) => (
          <span key={line} className={styles.tipLine}>
            {line}
          </span>
        ))}
      </ChartTooltip>
    </div>
  );
}
